import React, { FC, useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import Dropzone from "./Dropzone";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { TouchBackend } from "react-dnd-touch-backend";
import update from "immutability-helper";
import cuid from "cuid";
import ImageList from "./ImageList";
import { setGalleryFiles } from "../../../redux/actions/propertyActions";
import imageCompression from "browser-image-compression";
import { useDataProvider } from "react-admin";

export enum ImagesTypeEnum {
  DESKTOP = "desktop",
  MOBILE = "mobile",
  EMAIL_GALLERY = "EMAIL_GALLERY"
}

interface ImageModel {
  id: string;
  src: string;
}

interface ImageFiles {
  id: string;
  rawFile: File;
}

interface Props {
  imagesKey: Array<string> | undefined;
  error: boolean;
  awsFolder: string;
  isSingle?: boolean;
  title: string;
  imagesType?: ImagesTypeEnum;
}

const ImagesField: FC<Props> = ({
  imagesKey,
  error,
  awsFolder,
  isSingle,
  title,
  imagesType = ImagesTypeEnum.DESKTOP,
}) => {
  const dispatch = useDispatch();
  const dataProvider = useDataProvider();
  const [images, setImages] = useState(Array<any>());
  const [files, setFiles] = useState(Array<ImageFiles>());

  const [urlImg, setUrlImg] = useState();

  useEffect(() => {
    dispatch(setGalleryFiles(Array<File>(), imagesType));
    if (imagesKey) {
      getImages(imagesKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getImages = async (imagesKey: Array<string>) => {
    for (let i = 0; i < imagesKey.length; i++) {
      const data1 = await dataProvider.getImages({
        folder: awsFolder,
        image: imagesKey[i],
      });
      let file1 = new File([data1.data], "application/octet-stream");
      onDrop([file1]);
    }
  };

  const moveImage = (dragIndex, hoverIndex) => {
    const draggedImage = images[dragIndex];
    const draggedFile = files[dragIndex];
    const previousFiles = update(files, {
      $splice: [
        [dragIndex, 1],
        [hoverIndex, 0, draggedFile],
      ],
    });
    const dispatchFiles = previousFiles.reduce((acc, item) => {
      acc.push(item.rawFile);
      return acc;
    }, Array<File>());
    dispatch(setGalleryFiles(dispatchFiles, imagesType));
    setFiles(previousFiles);
    setImages(
      update(images, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, draggedImage],
        ],
      })
    );
  };

  const convertBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = () => {
        resolve(fileReader.result);
      };
      fileReader.onerror = (error) => {
        reject(error);
      };
    });
  };

  const updateImage = async (image: File, index: number) => {
    const currentImages = images;
    const options = {
      maxSizeMB: 0.3,
      maxWidthOrHeight: 1920,
    };

    image = await imageCompression(image, options);
    const currentImage = await convertBase64(image);
    currentImages[index].src = currentImage;
    setImages(currentImages);
    const currentFiles = files;
    currentFiles[index].rawFile = image;
    const dispatchFiles = currentFiles.reduce((acc, item) => {
      acc.push(item.rawFile);
      return acc;
    }, Array<File>());
    dispatch(setGalleryFiles(dispatchFiles, imagesType));
    setFiles(currentFiles);
    return currentFiles;
  };

  const removeImage = (index: number) => {
    const previosFiles = files;
    previosFiles.splice(index, 1);
    const dispatchFiles = previosFiles.reduce((acc, item) => {
      acc.push(item.rawFile);
      return acc;
    }, Array<File>());
    dispatch(setGalleryFiles(dispatchFiles, imagesType));
    setImages(
      update(images, {
        $splice: [[index, 1]],
      })
    );
  };

  const onDrop = useCallback((acceptedFiles) => {
    acceptedFiles.map((file) => {
      const reader = new FileReader();
      reader.onload = function (e: ProgressEvent<FileReader>) {
        const id = cuid();
        if (isSingle) {
          setImages([{ id: id, src: e.target?.result }]);
        } else {
          setImages((prevState) => [
            ...prevState,
            { id: id, src: e.target?.result },
          ]);
        }
        const currentFiles = isSingle ? Array<ImageFiles>() : files;
        const currentFile: ImageFiles = {
          id: id,
          rawFile: file,
        };
        currentFiles.push(currentFile);
        const dispatchFiles = currentFiles.reduce((acc, item) => {
          acc.push(item.rawFile);
          return acc;
        }, Array<File>());
        dispatch(setGalleryFiles(dispatchFiles, imagesType));
        setFiles(currentFiles);
      };
      reader.readAsDataURL(file);
      return file;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isTouchDevice = () => {
    if ("ontouchstart" in window) {
      return true;
    }
    return false;
  };

  const backendForDND = isTouchDevice() ? TouchBackend : HTML5Backend;
  return (
    <>
      <div className="base-color add-image-title">
        {`${title} *`}
        {/* <img src={}/> */}
      </div>
      <Dropzone onDrop={onDrop} accept={"image/*"} />
      {images && images.length > 1 && (
        <h3 className="base-color add-image-title">
          Drag the Images to change positions
        </h3>
      )}
      <DndProvider backend={backendForDND}>
        <ImageList
          images={images}
          moveImage={moveImage}
          removeItem={removeImage}
          updateImage={updateImage}
        />
        {error && images.length === 0 && (
          <div className="common-error">Required</div>
        )}
      </DndProvider>
    </>
  );
};

export default ImagesField;
